"use client";

import {
  keepPreviousData,
  useMutation,
  useQuery,
  useQueryClient,
} from "@tanstack/react-query";
import { useApi } from "@/lib/hooks/use-api";
import { queryKeys, type QueryFilters } from "@/lib/query-keys";

// ── Companies ────────────────────────────────────────────────────────────────

export const usePlatformCompaniesList = (filters: QueryFilters = {}) => {
  const api = useApi();
  return useQuery({
    queryKey: queryKeys.platform.companiesList(filters),
    queryFn: () => api.platform.listCompanies(filters as any),
    placeholderData: keepPreviousData,
  });
};

export const usePlatformCompany = (id?: string) => {
  const api = useApi();
  return useQuery({
    queryKey: queryKeys.platform.company(id || ""),
    enabled: Boolean(id),
    queryFn: async () => {
      if (!id) return null;
      const res = await api.platform.getCompanyById(id);
      return res?.data ?? null;
    },
  });
};

export const usePlatformCompanyMetrics = (id?: string) => {
  const api = useApi();
  return useQuery({
    queryKey: queryKeys.platform.companyMetrics(id || ""),
    enabled: Boolean(id),
    queryFn: async () => {
      if (!id) return null;
      const res = await api.platform.getCompanyMetrics(id);
      return res?.data ?? null;
    },
  });
};

export const useCreatePlatformCompany = () => {
  const queryClient = useQueryClient();
  const api = useApi();
  return useMutation({
    mutationFn: (payload: Parameters<typeof api.platform.createCompany>[0]) =>
      api.platform.createCompany(payload),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.platform.companies() });
    },
  });
};

export const useUpdatePlatformCompany = (id: string) => {
  const queryClient = useQueryClient();
  const api = useApi();
  return useMutation({
    mutationFn: (payload: Parameters<typeof api.platform.updateCompany>[1]) =>
      api.platform.updateCompany(id, payload),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.platform.company(id) });
      queryClient.invalidateQueries({ queryKey: queryKeys.platform.companies() });
    },
  });
};

export const useActivatePlatformCompany = () => {
  const queryClient = useQueryClient();
  const api = useApi();
  return useMutation({
    mutationFn: (id: string) => api.platform.activateCompany(id),
    onSuccess: (_data, id) => {
      queryClient.invalidateQueries({ queryKey: queryKeys.platform.company(id) });
      queryClient.invalidateQueries({ queryKey: queryKeys.platform.companies() });
    },
  });
};

export const useDeactivatePlatformCompany = () => {
  const queryClient = useQueryClient();
  const api = useApi();
  return useMutation({
    mutationFn: (id: string) => api.platform.deactivateCompany(id),
    onSuccess: (_data, id) => {
      queryClient.invalidateQueries({ queryKey: queryKeys.platform.company(id) });
      queryClient.invalidateQueries({ queryKey: queryKeys.platform.companies() });
    },
  });
};

// ── Reference profiles (platform) ───────────────────────────────────────────

/**
 * Fetch a platform reference profile (e.g. "organization-create" → countries).
 */
export const usePlatformReferenceProfile = (profile: string, enabled = true) => {
  const api = useApi();
  return useQuery({
    queryKey: queryKeys.platform.referenceProfile(profile),
    enabled,
    queryFn: () => api.platform.getReferenceProfile(profile),
  });
};

// ── Sub-resources of a company ───────────────────────────────────────────────

export const useCompanyDepartments = (companyId?: string, filters: QueryFilters = {}) => {
  const api = useApi();
  return useQuery({
    queryKey: queryKeys.platform.companyDepartments(companyId || "", filters),
    enabled: Boolean(companyId),
    queryFn: () => api.platform.listCompanyDepartments(companyId as string, filters),
  });
};

export const useCompanyUsers = (companyId?: string, filters: QueryFilters = {}) => {
  const api = useApi();
  return useQuery({
    queryKey: queryKeys.platform.companyUsers(companyId || "", filters),
    enabled: Boolean(companyId),
    queryFn: () => api.platform.listCompanyUsers(companyId as string, filters as any),
  });
};

export const useCreateCompanyUser = (companyId: string) => {
  const queryClient = useQueryClient();
  const api = useApi();
  return useMutation({
    mutationFn: (payload: {
      name: string;
      email: string;
      password: string;
      departmentId?: string;
    }) => api.platform.registerCompanyUser({ ...payload, companyId }),
    onSuccess: () => {
      // Invalidate the whole company subtree so the users list + any
      // membership-derived widgets refetch.
      queryClient.invalidateQueries({ queryKey: queryKeys.platform.company(companyId) });
    },
  });
};

export const useCompanyVendors = (companyId?: string, filters: QueryFilters = {}) => {
  const api = useApi();
  return useQuery({
    queryKey: queryKeys.platform.companyVendors(companyId || "", filters),
    enabled: Boolean(companyId),
    queryFn: () => api.platform.listCompanyVendors(companyId as string, filters),
  });
};

export const useCompanyCurrencies = (companyId?: string, filters: QueryFilters = {}) => {
  const api = useApi();
  return useQuery({
    queryKey: queryKeys.platform.companyCurrencies(companyId || "", filters),
    enabled: Boolean(companyId),
    queryFn: () => api.platform.listCompanyCurrencies(companyId as string, filters),
  });
};

export const useCompanyContacts = (companyId?: string, filters: QueryFilters = {}) => {
  const api = useApi();
  return useQuery({
    queryKey: queryKeys.platform.companyContacts(companyId || "", filters),
    enabled: Boolean(companyId),
    queryFn: () => api.platform.listCompanyContacts(companyId as string, filters),
  });
};

export const useCompanyCustomers = (companyId?: string, filters: QueryFilters = {}) => {
  const api = useApi();
  return useQuery({
    queryKey: queryKeys.platform.companyCustomers(companyId || "", filters),
    enabled: Boolean(companyId),
    queryFn: () => api.platform.listCompanyCustomers(companyId as string, filters),
  });
};

export const useCompanyShipments = (companyId?: string, filters: QueryFilters = {}) => {
  const api = useApi();
  return useQuery({
    queryKey: queryKeys.platform.companyShipments(companyId || "", filters),
    enabled: Boolean(companyId),
    queryFn: () => api.platform.listShipments(companyId as string, filters),
  });
};

export const useCompanyDrivers = (companyId?: string, filters: QueryFilters = {}) => {
  const api = useApi();
  return useQuery({
    queryKey: queryKeys.platform.companyDrivers(companyId || "", filters),
    enabled: Boolean(companyId),
    queryFn: () => api.platform.listDrivers(companyId as string, filters),
  });
};

export const useCompanyVehicles = (companyId?: string, filters: QueryFilters = {}) => {
  const api = useApi();
  return useQuery({
    queryKey: queryKeys.platform.companyVehicles(companyId || "", filters),
    enabled: Boolean(companyId),
    queryFn: () => api.platform.listVehicles(companyId as string, filters),
  });
};

export const useCompanyQuotes = (companyId?: string, filters: QueryFilters = {}) => {
  const api = useApi();
  return useQuery({
    queryKey: queryKeys.platform.companyQuotes(companyId || "", filters),
    enabled: Boolean(companyId),
    queryFn: () => api.platform.listCompanyQuotes(companyId as string, filters),
  });
};

export const useCompanyCarriers = (companyId?: string, filters: QueryFilters = {}) => {
  const api = useApi();
  return useQuery({
    queryKey: queryKeys.platform.companyCarriers(companyId || "", filters),
    enabled: Boolean(companyId),
    queryFn: () => api.platform.listCompanyCarriers(companyId as string, filters),
  });
};

export const useCompanyClients = (companyId?: string, filters: QueryFilters = {}) => {
  const api = useApi();
  return useQuery({
    queryKey: queryKeys.platform.companyClients(companyId || "", filters),
    enabled: Boolean(companyId),
    queryFn: () => api.platform.listCompanyClients(companyId as string, filters),
  });
};

export const useCompanyOrders = (companyId?: string, filters: QueryFilters = {}) => {
  const api = useApi();
  return useQuery({
    queryKey: queryKeys.platform.companyOrders(companyId || "", filters),
    enabled: Boolean(companyId),
    queryFn: () => api.platform.listCompanyOrders(companyId as string, filters),
  });
};

export const useCompanyAddresses = (companyId?: string, filters: QueryFilters = {}) => {
  const api = useApi();
  return useQuery({
    queryKey: queryKeys.platform.companyAddresses(companyId || "", filters),
    enabled: Boolean(companyId),
    queryFn: () => api.platform.listCompanyAddresses(companyId as string, filters),
  });
};

export const useCompanyRegions = (companyId?: string, filters: QueryFilters = {}) => {
  const api = useApi();
  return useQuery({
    queryKey: queryKeys.platform.companyRegions(companyId || "", filters),
    enabled: Boolean(companyId),
    queryFn: () => api.platform.listCompanyRegions(companyId as string, filters),
  });
};

export const useCompanyQuoteSources = (companyId?: string, filters: QueryFilters = {}) => {
  const api = useApi();
  return useQuery({
    queryKey: queryKeys.platform.companyQuoteSources(companyId || "", filters),
    enabled: Boolean(companyId),
    queryFn: () => api.platform.listCompanyQuoteSources(companyId as string, filters),
  });
};

export const useCompanyClientTypes = (companyId?: string, filters: QueryFilters = {}) => {
  const api = useApi();
  return useQuery({
    queryKey: queryKeys.platform.companyClientTypes(companyId || "", filters),
    enabled: Boolean(companyId),
    queryFn: () => api.platform.listCompanyClientTypes(companyId as string, filters),
  });
};

export const useCompanyEntityRatings = (companyId?: string, filters: QueryFilters = {}) => {
  const api = useApi();
  return useQuery({
    queryKey: queryKeys.platform.companyEntityRatings(companyId || "", filters),
    enabled: Boolean(companyId),
    queryFn: () => api.platform.listCompanyEntityRatings(companyId as string, filters),
  });
};

export const useCompanyRefusalReasons = (companyId?: string, filters: QueryFilters = {}) => {
  const api = useApi();
  return useQuery({
    queryKey: queryKeys.platform.companyRefusalReasons(companyId || "", filters),
    enabled: Boolean(companyId),
    queryFn: () => api.platform.listCompanyRefusalReasons(companyId as string, filters),
  });
};

export const useCompanyContactPositions = (companyId?: string, filters: QueryFilters = {}) => {
  const api = useApi();
  return useQuery({
    queryKey: queryKeys.platform.companyContactPositions(companyId || "", filters),
    enabled: Boolean(companyId),
    queryFn: () => api.platform.listCompanyContactPositions(companyId as string, filters),
  });
};

export const useCompanyCargos = (companyId?: string, filters: QueryFilters = {}) => {
  const api = useApi();
  return useQuery({
    queryKey: queryKeys.platform.companyCargos(companyId || "", filters),
    enabled: Boolean(companyId),
    queryFn: () => api.platform.listCompanyCargos(companyId as string, filters),
  });
};

// Department mutations (scoped to a company)

export const useCreateCompanyDepartment = (companyId: string) => {
  const queryClient = useQueryClient();
  const api = useApi();
  return useMutation({
    mutationFn: (payload: Parameters<typeof api.platform.createCompanyDepartment>[1]) =>
      api.platform.createCompanyDepartment(companyId, payload),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: queryKeys.platform.companyDepartments(companyId, {}),
      });
    },
  });
};

export const useUpdateCompanyDepartment = (companyId: string) => {
  const queryClient = useQueryClient();
  const api = useApi();
  return useMutation({
    mutationFn: ({
      id,
      payload,
    }: {
      id: string;
      payload: Parameters<typeof api.platform.updateCompanyDepartment>[2];
    }) => api.platform.updateCompanyDepartment(companyId, id, payload),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: queryKeys.platform.companyDepartments(companyId, {}),
      });
    },
  });
};

export const useDeleteCompanyDepartment = (companyId: string) => {
  const queryClient = useQueryClient();
  const api = useApi();
  return useMutation({
    mutationFn: (id: string) => api.platform.deleteCompanyDepartment(companyId, id),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: queryKeys.platform.companyDepartments(companyId, {}),
      });
    },
  });
};

/**
 * Every active device session across an organization's users, for the
 * org-detail Sessions tab. The component groups by user → Web / Mobile.
 */
export const useCompanySessions = (companyId?: string) => {
  const api = useApi();
  return useQuery({
    queryKey: queryKeys.platform.companySessions(companyId || ""),
    enabled: Boolean(companyId),
    queryFn: () => api.platform.listCompanySessions(companyId as string),
    staleTime: 30_000,
  });
};

/** Force-revoke a session, refreshing the org Sessions tab. */
export const useRevokeCompanySession = (companyId: string) => {
  const queryClient = useQueryClient();
  const api = useApi();
  return useMutation({
    mutationFn: (sessionId: string) => api.platform.revokeSession(sessionId),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: queryKeys.platform.companySessions(companyId),
      });
    },
  });
};
